import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBell,
    faArrowLeft,
    faInfoCircle,
    faCheckCircle,
    faBirthdayCake,
    faCalendarAlt,
    faBullseye,
    faEye,
    faEyeSlash,
    faGift
} from '@fortawesome/free-solid-svg-icons';
import DocumentationLayout from '../../../components/documentation/DocumentationLayout';

function FeedbackAlertas() {
    return (
        <DocumentationLayout>
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-gray-800 mb-4">
                        Centro de Alertas
                    </h1>
                    <p className="text-xl text-gray-600">
                        Mantente informado sobre eventos importantes de tu negocio y actúa rápidamente para fortalecer la relación con tus clientes.
                    </p>
                </div>

                {/* Acceso */}
                <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-3 flex items-center">
                        <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-blue-600" />
                        ¿Cómo acceder?
                    </h2>
                    <p className="text-gray-700 mb-2">
                        Para acceder al Centro de Alertas:
                    </p>
                    <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
                        <li>Inicia sesión en tu cuenta de PetitPrime</li>
                        <li>En el menú principal, busca la sección <strong>"Alertas"</strong> o <strong>"Feedback"</strong></li>
                        <li>Haz clic en <strong>"Centro de Alertas"</strong></li>
                        <li>O accede directamente a: <code className="bg-white px-2 py-1 rounded">/alertas</code></li>
                    </ol>
                    <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                        <p className="text-sm text-gray-700">
                            <strong>Nota:</strong> También recibirás notificaciones automáticas en el header de la aplicación cuando haya alertas nuevas no leídas.
                        </p>
                    </div>
                </div>

                {/* Tipos de Alertas */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        1. Tipos de Alertas
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            El sistema genera automáticamente diferentes tipos de alertas según eventos importantes:
                        </p>

                        <div className="space-y-4">
                            <div className="bg-pink-50 border-l-4 border-pink-500 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faBirthdayCake} className="mr-2 text-pink-600" />
                                    Alertas de Cumpleaños
                                </h3>
                                <p className="text-sm text-gray-700 mb-2">
                                    Se generan automáticamente cuando un cliente tiene cumpleaños. Estas alertas son perfectas para:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>Enviar felicitaciones personalizadas</li>
                                    <li>Ofrecer descuentos o regalos especiales</li>
                                    <li>Fortalecer la relación con clientes valiosos</li>
                                    <li>Incrementar la fidelización</li>
                                </ul>
                            </div>

                            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-blue-600" />
                                    Alertas de Fecha Clave
                                </h3>
                                <p className="text-sm text-gray-700 mb-2">
                                    Alertas relacionadas con eventos importantes o fechas especiales de tu negocio:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>Aniversarios de clientes (fecha de primera compra)</li>
                                    <li>Eventos especiales de tu marca</li>
                                    <li>Promociones temporales</li>
                                    <li>Cualquier fecha importante que hayas configurado</li>
                                </ul>
                            </div>

                            <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faBullseye} className="mr-2 text-green-600" />
                                    Alertas de Objetivos
                                </h3>
                                <p className="text-sm text-gray-700 mb-2">
                                    Notificaciones cuando se alcanzan objetivos importantes:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>Clientes que alcanzan cierta cantidad de puntos</li>
                                    <li>Objetivos de ventas alcanzados</li>
                                    <li>Hitos de fidelización</li>
                                    <li>Metas de participación en campañas</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Resumen de Alertas */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        2. Resumen de Alertas
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            En la parte superior del Centro de Alertas verás tres tarjetas con un resumen:
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <h3 className="font-semibold text-red-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faEye} className="mr-2" />
                                    No Leídas
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Muestra el número total de alertas que aún no has revisado. Estas son las más importantes y requieren tu atención.
                                </p>
                            </div>

                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <h3 className="font-semibold text-green-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faEyeSlash} className="mr-2" />
                                    Leídas
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Total de alertas que ya has revisado. Puedes ocultarlas o mostrarlas según necesites.
                                </p>
                            </div>

                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <h3 className="font-semibold text-blue-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
                                    Total
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Suma total de todas las alertas (leídas y no leídas) asociadas a tus marcas.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Gestión de Alertas */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        3. Gestionar Alertas
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Las alertas se organizan automáticamente por tipo y estado:
                        </p>

                        {/* Mostrar/Ocultar Leídas */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">
                                Mostrar/Ocultar Alertas Leídas
                            </h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-700 mb-2">
                                    En la parte superior derecha encontrarás un botón para mostrar u ocultar las alertas leídas:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li><strong>Ocultar (por defecto):</strong> Solo verás alertas no leídas para mantener el foco en lo importante</li>
                                    <li><strong>Mostrar:</strong> Verás todas las alertas, incluyendo las que ya revisaste</li>
                                </ul>
                            </div>
                        </div>

                        {/* Organización por Tipo */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">
                                Organización por Tipo
                            </h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-700 mb-2">
                                    Las alertas se agrupan automáticamente por tipo (Cumpleaños, Fecha Clave, Objetivos) y se muestran en secciones separadas con colores distintivos:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>Cada tipo tiene su propio color de fondo y icono</li>
                                    <li>Las alertas no leídas aparecen primero</li>
                                    <li>Cada sección muestra el número de alertas de ese tipo</li>
                                </ul>
                            </div>
                        </div>

                        {/* Información de cada Alerta */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">
                                Información de cada Alerta
                            </h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-700 mb-2">
                                    Cada alerta muestra:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li><strong>Mensaje:</strong> Descripción clara del evento o situación</li>
                                    <li><strong>Marca:</strong> A qué marca pertenece la alerta</li>
                                    <li><strong>Fecha:</strong> Cuándo se generó la alerta</li>
                                    <li><strong>Cliente (si aplica):</strong> Información del cliente relacionado</li>
                                    <li><strong>Botón de acción:</strong> Para marcar como leída o enviar recompensas</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Acciones Disponibles */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        4. Acciones Disponibles
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Para cada alerta puedes realizar las siguientes acciones:
                        </p>

                        <div className="space-y-4">
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faEyeSlash} className="mr-2 text-blue-600" />
                                    Marcar como Leída
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Haz clic en el botón <strong>"Marcar como leída"</strong> para indicar que ya has revisado la alerta. Esto la moverá a la sección de alertas leídas y reducirá el contador de alertas no leídas.
                                </p>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faGift} className="mr-2 text-green-600" />
                                    Enviar Recompensa
                                </h3>
                                <p className="text-sm text-gray-600 mb-2">
                                    Si la alerta está relacionada con un cliente (como cumpleaños u objetivos), verás un botón para <strong>"Enviar Recompensa"</strong>:
                                </p>
                                <ol className="list-decimal list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>Haz clic en el botón de enviar recompensa</li>
                                    <li>Se abrirá un modal con las recompensas disponibles</li>
                                    <li>Selecciona la recompensa que deseas enviar</li>
                                    <li>Confirma el envío</li>
                                    <li>El cliente recibirá la recompensa automáticamente</li>
                                </ol>
                                <p className="text-sm text-gray-600 mt-2">
                                    <strong>Ideal para:</strong> Cumpleaños, aniversarios, objetivos alcanzados y cualquier ocasión especial.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Mejores Prácticas */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        5. Mejores Prácticas
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <ul className="space-y-4">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Revisa las alertas diariamente:</strong> Dedica unos minutos cada día para revisar las alertas nuevas y tomar acción sobre ellas.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Aprovecha los cumpleaños:</strong> Los cumpleaños son oportunidades perfectas para fortalecer la relación con tus clientes. Envía una recompensa especial o un mensaje personalizado.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Celebra los objetivos alcanzados:</strong> Cuando un cliente alcanza un objetivo (como cierta cantidad de puntos), reconócelo enviando una recompensa. Esto aumenta la motivación y fidelización.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Mantén el centro limpio:</strong> Marca las alertas como leídas después de revisarlas para mantener el dashboard organizado y enfocado en lo importante.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Actúa rápidamente:</strong> Las alertas de cumpleaños y eventos tienen más impacto cuando actúas el mismo día. No dejes pasar demasiado tiempo.
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Notificaciones Automáticas */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        6. Notificaciones Automáticas
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Además del Centro de Alertas, también recibirás notificaciones automáticas:
                        </p>
                        <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                            <li><strong>En el Header:</strong> Verás un indicador con el número de alertas no leídas en la parte superior de la aplicación</li>
                            <li><strong>Modal de Alertas:</strong> Al iniciar sesión, si hay alertas nuevas importantes, se mostrará un modal con las alertas más relevantes</li>
                            <li><strong>Actualización en Tiempo Real:</strong> Las alertas se actualizan automáticamente cuando se generan nuevos eventos</li>
                        </ul>
                        <div className="mt-4 bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                            <p className="text-sm text-gray-700">
                                <strong>💡 Tip:</strong> Haz clic en el indicador de alertas del header para acceder rápidamente al Centro de Alertas.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Troubleshooting */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        7. Solución de Problemas
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="space-y-4">
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">No veo alertas en el centro</h4>
                                <p className="text-gray-700 text-sm">
                                    Es normal si acabas de crear tu cuenta o si no hay eventos recientes. Las alertas se generan automáticamente cuando ocurren eventos como cumpleaños de clientes o cuando se alcanzan objetivos.
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">Las alertas no se actualizan</h4>
                                <p className="text-gray-700 text-sm">
                                    Refresca la página para ver las alertas más recientes. Las alertas se generan en tiempo real cuando ocurren los eventos.
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">No puedo enviar recompensas desde una alerta</h4>
                                <p className="text-gray-700 text-sm">
                                    Verifica que la alerta esté asociada a un cliente específico. Algunas alertas generales (como eventos de marca) no tienen cliente asociado y no permiten enviar recompensas individuales.
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">Las alertas de cumpleaños no aparecen</h4>
                                <p className="text-gray-700 text-sm">
                                    Asegúrate de que tus clientes tengan su fecha de cumpleaños registrada en el sistema. Las alertas se generan automáticamente cuando un cliente tiene cumpleaños.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Navegación */}
                <div className="flex justify-between items-center pt-8 border-t">
                    <Link
                        to="/documentation/feedback/encuestas"
                        className="inline-flex items-center text-purple-600 hover:text-purple-800 font-medium"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                        Anterior: Encuestas
                    </Link>
                    <Link
                        to="/documentation/feedback"
                        className="inline-flex items-center text-purple-600 hover:text-purple-800 font-medium"
                    >
                        Volver a Feedback y Alertas
                    </Link>
                </div>
            </div>
        </DocumentationLayout>
    );
}

export default FeedbackAlertas;

